import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

const ContentApproval: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = useState<"pending" | "approved" | "rejected">("pending")
  const [selectedCreator, setSelectedCreator] = useState<string>("")
  const [selectedPlatform, setSelectedPlatform] = useState<"linkedin" | "twitter" | "">("")
  const [selectedContent, setSelectedContent] = useState<any>(null)

  const {
    data: scrapedContent,
    isLoading,
    refetch,
  } = trpc.contentManagement.listScrapedContent.useQuery({
    status: selectedStatus,
    creatorId: selectedCreator || undefined,
    platform: selectedPlatform || undefined,
    limit: 50,
  })

  const { data: creators } = trpc.contentManagement.listCreators.useQuery()

  const approveAsIdeaMutation = trpc.contentManagement.approveAsIdea.useMutation()
  const approveAsTemplateMutation = trpc.contentManagement.approveAsTemplate.useMutation()
  const rejectContentMutation = trpc.contentManagement.rejectScrapedContent.useMutation()

  const handleApproveAsIdea = async (scrapedId: string, ideaData: any) => {
    try {
      await approveAsIdeaMutation.mutateAsync({ scrapedId, ...ideaData })
      void refetch()
      setSelectedContent(null)
    } catch (error) {
      console.error("Failed to approve as idea:", error)
    }
  }

  const handleApproveAsTemplate = async (scrapedId: string, templateData: any) => {
    try {
      await approveAsTemplateMutation.mutateAsync({ scrapedId, ...templateData })
      void refetch()
      setSelectedContent(null)
    } catch (error) {
      console.error("Failed to approve as template:", error)
    }
  }

  const handleReject = async (scrapedId: string, reason: string) => {
    try {
      await rejectContentMutation.mutateAsync({ scrapedId })
      void refetch()
      setSelectedContent(null)
    } catch (error) {
      console.error("Failed to reject content:", error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold ">Content Approval</h2>
            <p className="">Review scraped content and approve as ideas or templates</p>
          </div>

          {/* Status Filter */}
          <div className="flex bg-foregroundx rounded-lg p-1">
            {(["pending", "approved", "rejected"] as const).map((status) => (
              <button
                key={status}
                onClick={() => setSelectedStatus(status)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedStatus === status ? "bg-background  shadow-sm" : " hover:"
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Additional Filters */}
        <div className="flex space-x-4">
          {/* Creator Filter */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-primary mb-1">Creator</label>
            <select
              value={selectedCreator}
              onChange={(e) => setSelectedCreator(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Creators</option>
              {creators?.map((creator: any) => (
                <option key={creator.id} value={creator.id}>
                  {creator.name} (@{creator.handle})
                </option>
              ))}
            </select>
          </div>

          {/* Platform Filter */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-primary mb-1">Platform</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value as "linkedin" | "twitter" | "")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Platforms</option>
              <option value="linkedin">LinkedIn</option>
              <option value="twitter">Twitter</option>
            </select>
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={() => {
                setSelectedCreator("")
                setSelectedPlatform("")
              }}
              className="px-4 py-2 text-sm  hover: border border-gray-300 rounded-md "
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {scrapedContent?.map((item: any) => (
          <div key={item.scraped.id} className="bg-background p-6 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-foregroundx rounded-full flex items-center justify-center">
                  <span className="text-sm">{item.scraped.platform === "linkedin" ? "💼" : "🐦"}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 className="font-medium ">{item.creator.name}</h4>
                    {item.scraped.originalUrl && (
                      <a
                        href={item.scraped.originalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className=" hover:text-blue-800 text-sm"
                        title="View original post"
                      >
                        🔗
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">@{item.creator.handle}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    item.scraped.status === "pending"
                      ? "bg-yellow-100 text-yellow-800"
                      : item.scraped.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-destructive"
                  }`}
                >
                  {item.scraped.status}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-primary text-sm line-clamp-4">{item.scraped.content}</p>
            </div>

            {/* Engagement Metrics */}
            <div className="flex justify-between text-xs text-gray-500 mb-4">
              <span>❤️ {item.scraped.engagementMetrics?.likes || 0}</span>
              <span>💬 {item.scraped.engagementMetrics?.comments || 0}</span>
              <span>🔄 {item.scraped.engagementMetrics?.retweets || 0}</span>
              <span>📅 {new Date(item.scraped.scrapedAt).toLocaleDateString()}</span>
            </div>

            {/* LLM Analysis Preview */}
            {item.scraped.llmAnalysis && (
              <div className="bg-foregroundx p-3 rounded mb-4">
                <div className="text-xs  mb-2">AI Analysis:</div>
                <div className="text-xs space-y-1">
                  <div>
                    <strong>Main Idea:</strong> {item.scraped.llmAnalysis.main_idea?.substring(0, 80)}...
                  </div>
                  <div>
                    <strong>Hook Style:</strong> {item.scraped.llmAnalysis.hook_style}
                  </div>
                  {item.scraped.llmAnalysis.tags && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {item.scraped.llmAnalysis.tags.slice(0, 3).map((tag: string, idx: number) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {item.scraped.status === "pending" && (
              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedContent(item)}
                  className="flex-1 bg-blue-100 text-blue-700 py-2 px-3 rounded text-sm hover:bg-blue-200 transition-colors"
                >
                  Review
                </button>
              </div>
            )}

            {item.scraped.status !== "pending" && (
              <div className="text-center py-2">
                <span className="text-sm text-gray-500">
                  {item.scraped.status === "approved" ? "✅ Approved" : "❌ Rejected"}
                </span>
              </div>
            )}
          </div>
        ))}

        {(!scrapedContent || scrapedContent.length === 0) && (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">📋</div>
            <h3 className="text-lg font-medium  mb-2">No content to review</h3>
            <p className="">
              {selectedStatus === "pending"
                ? "No pending content. Start scraping creators to get content for review."
                : `No ${selectedStatus} content found.`}
            </p>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {selectedContent && (
        <ReviewModal
          content={selectedContent}
          onClose={() => setSelectedContent(null)}
          onApproveAsIdea={(ideaData) => handleApproveAsIdea(selectedContent.scraped.id, ideaData)}
          onApproveAsTemplate={(templateData) => handleApproveAsTemplate(selectedContent.scraped.id, templateData)}
          onReject={(reason) => handleReject(selectedContent.scraped.id, reason)}
          isLoading={
            approveAsIdeaMutation.isLoading || approveAsTemplateMutation.isLoading || rejectContentMutation.isLoading
          }
        />
      )}
    </div>
  )
}

const ReviewModal: React.FC<{
  content: any
  onClose: () => void
  onApproveAsIdea: (data: any) => void
  onApproveAsTemplate: (data: any) => void
  onReject: (reason: string) => void
  isLoading: boolean
}> = ({ content, onClose, onApproveAsIdea, onApproveAsTemplate, onReject, isLoading }) => {
  const [activeTab, setActiveTab] = useState<"content" | "idea" | "template" | "reject">("content")
  const [ideaData, setIdeaData] = useState({
    title: "",
    description: "",
    tags: [] as string[],
    domains: {} as Record<string, number>,
    priority: "medium",
  })
  const [templateData, setTemplateData] = useState({
    name: "",
    description: "",
    structure: "",
    variables: [] as string[],
    category: "general",
  })
  const [rejectReason, setRejectReason] = useState("")
  const [newTag, setNewTag] = useState("")

  const analysis = content.scraped.llmAnalysis || {}

  // Pre-populate data from LLM analysis
  React.useEffect(() => {
    if (analysis.main_idea) {
      setIdeaData((prev) => ({
        ...prev,
        title: analysis.main_idea?.substring(0, 100) || "",
        tags: analysis.tags || [],
        domains: analysis.domains || {},
      }))
    }
    if (analysis.content_structure) {
      setTemplateData((prev) => ({
        ...prev,
        structure: analysis.content_structure || "",
      }))
    }
  }, [analysis])

  const addTag = () => {
    if (newTag.trim() && !ideaData.tags.includes(newTag.trim())) {
      setIdeaData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }))
      setNewTag("")
    }
  }

  const removeTag = (tagToRemove: string) => {
    setIdeaData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }))
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium ">Review Content</h3>
              <div className="flex items-center space-x-2">
                <p className="text-sm ">
                  From {content.creator.name} (@{content.creator.handle}) on {content.scraped.platform}
                </p>
                {content.scraped.originalUrl && (
                  <a
                    href={content.scraped.originalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=" hover:text-blue-800 text-sm underline"
                  >
                    View Original Post
                  </a>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:">
              ✕
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <nav className="flex space-x-8 px-6">
            {[
              { id: "content", label: "Content", icon: "📄" },
              { id: "idea", label: "Approve as Idea", icon: "💡" },
              { id: "template", label: "Approve as Template", icon: "📝" },
              { id: "reject", label: "Reject", icon: "❌" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id ? "border-blue-500 " : "border-transparent text-gray-500 hover:text-primary"
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === "content" && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium  mb-2">Original Content</h4>
                <div className="bg-foregroundx p-4 rounded-lg">
                  <p className="text-primary">{content.scraped.content}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium  mb-2">Engagement Metrics</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Likes:</span>
                      <span>{content.scraped.engagementMetrics?.likes || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Comments:</span>
                      <span>{content.scraped.engagementMetrics?.comments || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Shares:</span>
                      <span>{content.scraped.engagementMetrics?.retweets || 0}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium  mb-2">AI Analysis</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <strong>Hook Style:</strong> {analysis.hook_style}
                    </div>
                    <div>
                      <strong>Template Potential:</strong> {analysis.template_potential}
                    </div>
                    <div>
                      <strong>Idea Potential:</strong> {analysis.idea_potential}
                    </div>
                  </div>
                </div>
              </div>

              {analysis.tags && (
                <div>
                  <h4 className="font-medium  mb-2">Suggested Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {analysis.tags.map((tag: string, idx: number) => (
                      <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "idea" && (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                onApproveAsIdea(ideaData)
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Idea Title</label>
                <input
                  type="text"
                  required
                  value={ideaData.title}
                  onChange={(e) => setIdeaData({ ...ideaData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Description</label>
                <textarea
                  value={ideaData.description}
                  onChange={(e) => setIdeaData({ ...ideaData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Tags</label>
                <div className="flex space-x-2 mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                    placeholder="Add tag..."
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ideaData.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-foregroundx text-primary text-sm rounded-full flex items-center"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-2 text-destructive hover:text-destructive"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Priority</label>
                <select
                  value={ideaData.priority}
                  onChange={(e) => setIdeaData({ ...ideaData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? "Approving..." : "Approve as Idea"}
                </button>
              </div>
            </form>
          )}

          {activeTab === "template" && (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                onApproveAsTemplate(templateData)
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Template Name</label>
                <input
                  type="text"
                  required
                  value={templateData.name}
                  onChange={(e) => setTemplateData({ ...templateData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Description</label>
                <textarea
                  value={templateData.description}
                  onChange={(e) => setTemplateData({ ...templateData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Template Structure</label>
                <textarea
                  required
                  value={templateData.structure}
                  onChange={(e) => setTemplateData({ ...templateData, structure: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  rows={5}
                  placeholder="Define the template structure with variables like {{company}}, {{benefit}}, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Category</label>
                <select
                  value={templateData.category}
                  onChange={(e) => setTemplateData({ ...templateData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                >
                  <option value="general">General</option>
                  <option value="product_launch">Product Launch</option>
                  <option value="thought_leadership">Thought Leadership</option>
                  <option value="engagement">Engagement</option>
                  <option value="announcement">Announcement</option>
                </select>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? "Approving..." : "Approve as Template"}
                </button>
              </div>
            </form>
          )}

          {activeTab === "reject" && (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                onReject(rejectReason)
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Reason for Rejection</label>
                <textarea
                  required
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  placeholder="Explain why this content is being rejected..."
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? "Rejecting..." : "Reject Content"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default ContentApproval
